import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ReferenceLine, ReferenceArea, ComposedChart, Area,
} from 'recharts';
import { TrendingUp, Loader2, BarChart3, Users, Layers, GitCompare, Download, Mail, Sparkles, RefreshCw, History, Save, X, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component lines drawn on the composite graph — People Engine model.
const COMPONENT_LINES = [
  { key: 'capability', color: '#6366f1', name: 'Capability' },
  { key: 'motivation', color: '#f59e0b', name: 'Motivation' },
  { key: 'retention', color: '#10b981', name: 'Retention' },
];
const LEGEND_RANK = { score: 0, capability: 1, motivation: 2, retention: 3 };
const ANNO_COLOR = { program: '#0891b2', content: '#d97706', mixed: '#7c3aed' };
const ANNO_LABELS = { all: 'All', program: 'Launches', content: 'Content' };
const SUB_COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ec4899', '#3b82f6', '#8b5cf6'];

// Per-component drill-in ("PE Trends by Component") — which sub-lines to plot
// from the /diagnostic/{component} endpoint, and how to normalise them to 0–100.
const COMPONENT_DETAIL = {
  capability: { series: [{ key: 'score', name: 'Capability', scale: 100 }] },
  motivation: {
    series: [
      { key: 'total', name: 'Motivation (total)', scale: 1 },
      { key: 'intrinsic', name: 'Intrinsic', scale: 1 },
      { key: 'extrinsic', name: 'Extrinsic', scale: 1 },
    ],
  },
  retention: { series: [{ key: 'rate', name: 'Retention rate', scale: 'auto' }] },
};

function AnnotationMarker({ viewBox, anno, color, index, last, onClick }) {
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  const short = anno.label.length > 14 ? `${anno.label.slice(0, 13)}…` : anno.label;
  const anchor = last ? 'end' : 'middle';
  const tx = last ? x - 6 : x;
  const ty = index % 2 === 0 ? y - 4 : y - 17;
  return (
    <g data-testid={`pe-full-annotation-${index}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <title>{`${anno.label}${anno.detail ? ` — ${anno.detail}` : ''}${onClick ? ' (click to open)' : ''}`}</title>
      <rect x={x - 8} y={y} width={16} height={height} fill="transparent" pointerEvents="all" />
      <circle cx={x} cy={y + 4} r={4} fill={color} />
      <text x={tx} y={ty} textAnchor={anchor} fontSize={9} fontWeight={600} fill={color}>{short}</text>
    </g>
  );
}

// Compute a month→projected-PE map for a given driver lift, applied to the
// forecast trajectory (ramped over the horizon) with implied LEI held fixed.
function computeScenarioByMonth(forecastData, lift) {
  if (!forecastData) return {};
  const { horizon: H, histLen, data } = forecastData;
  const out = {};
  let step = 0;
  data.forEach((row) => {
    if (row.forecast == null) return;
    step += 1;
    const ramp = step / H;
    const capL = Math.min(100, (row.cap_f ?? 0) + (lift.capability || 0) * ramp);
    const motL = Math.min(100, (row.mot_f ?? 0) + (lift.motivation || 0) * ramp);
    const retL = Math.min(100, (row.ret_f ?? 0) + (lift.retention || 0) * ramp);
    const c = (row.cap_f ?? 0) / 100, m = (row.mot_f ?? 0) / 100, r = (row.ret_f ?? 0) / 100, pe = (row.forecast ?? 0) / 100;
    const impliedLei = Math.max(0, Math.min(1, 2 * pe - c * m * r));
    const scen = (((capL / 100) * (motL / 100) * (retL / 100)) + impliedLei) / 2 * 100;
    out[row.month] = Math.round(Math.max(0, Math.min(100, scen)));
  });
  if (data[histLen]) out[data[histLen].month] = data[histLen].actual;
  return out;
}

const CMP_COLORS = ['#0ea5e9', '#f43f5e'];

export default function PeTrendsFull({ organizationId, cohortId = 'all', navigate, onlyByComponent = false }) {
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState(cohortId || 'all');
  const [compareDim, setCompareDim] = useState('cohort'); // cohort | program | period
  const [compareCohort, setCompareCohort] = useState('none');
  const [comparePid, setComparePid] = useState('none');
  const [periodA, setPeriodA] = useState({ start: '', end: '' });
  const [periodB, setPeriodB] = useState({ start: '', end: '' });
  const [trend, setTrend] = useState(null);
  const [compareSeries, setCompareSeries] = useState(null);
  const [periodSeries, setPeriodSeries] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState('capability');
  const [insight, setInsight] = useState(null);
  const [insightCached, setInsightCached] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [compareSel, setCompareSel] = useState([]);
  const toggleCompare = useCallback((i) => {
    setCompareSel((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i].slice(-2)));
  }, []);
  const [hidden, setHidden] = useState(() => new Set());
  const [annoFilter, setAnnoFilter] = useState('all');
  const [showContrib, setShowContrib] = useState(false);
  const [exporting, setExporting] = useState(false);
  // What-if intervention lift (pts) applied to the forecast, per component.
  const [lift, setLift] = useState({ capability: 0, motivation: 0, retention: 0 });
  const scenarioActive = lift.capability > 0 || lift.motivation > 0 || lift.retention > 0;
  const resetLift = useCallback(() => setLift({ capability: 0, motivation: 0, retention: 0 }), []);
  const [scenarios, setScenarios] = useState([]);
  const [scenarioName, setScenarioName] = useState('');
  const [scenarioCost, setScenarioCost] = useState('');
  const [savingScenario, setSavingScenario] = useState(false);
  const [showSaveScenario, setShowSaveScenario] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const toggleCompareScenario = useCallback((id) => {
    setCompareIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id].slice(-2)));
  }, []);
  const [emailSending, setEmailSending] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const compositeRef = useRef(null);
  const byComponentRef = useRef(null);

  const h = useMemo(() => {
    const token = secureStorage.getItem('session_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const orgParam = useCallback(() => (organizationId && organizationId !== 'all' ? `organization_id=${encodeURIComponent(organizationId)}` : ''), [organizationId]);

  // Load cohorts + programs once (scoped by org).
  useEffect(() => {
    const q = orgParam();
    authedFetch(`${API_URL}/api/people-engine/cohorts?${q}`, h)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setCohorts(d?.cohorts || [])).catch(() => setCohorts([]));
    authedFetch(`${API_URL}/api/lei-health/program-pillars?${q}`, h)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setPrograms((d?.cohorts || []).map((p) => ({ id: p.cohort_id, name: p.cohort_name })))).catch(() => setPrograms([]));
    Promise.all([
      authedFetch(`${API_URL}/api/people-engine/report-recipients?${q}`, h).then((r) => (r.ok ? r.json() : null)),
      authedFetch(`${API_URL}/api/people-engine/report-schedule?${q}`, h).then((r) => (r.ok ? r.json() : null)),
    ]).then(([rec, sch]) => {
      const saved = (sch?.recipients || []);
      setEmailRecipients((saved.length ? saved : (rec?.recipients || [])).join(', '));
      setScheduleEnabled(!!sch?.enabled);
    }).catch(() => {});
  }, [orgParam, h]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      if (selectedCohort && selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
      const [trRes, dtRes] = await Promise.all([
        authedFetch(`${API_URL}/api/people-engine/pe-trend?${params}`, h),
        authedFetch(`${API_URL}/api/people-engine/diagnostic/${selectedComponent}?${params}`, h),
      ]);
      setTrend(trRes.ok ? await trRes.json() : null);
      setDetail(dtRes.ok ? await dtRes.json() : null);
      // Auto-load a cached AI trend summary (never triggers a fresh spend).
      authedFetch(`${API_URL}/api/people-engine/pe-trend-insight`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: organizationId, cohort_id: selectedCohort, cached_only: true }),
      }).then((r) => (r.ok ? r.json() : null)).then((d) => {
        setInsight(d?.available ? d.insight : null);
        setInsightCached(!!d?.available);
      }).catch(() => { setInsight(null); setInsightCached(false); });
    } catch { setTrend(null); setDetail(null); }
    finally { setLoading(false); }
  }, [organizationId, selectedCohort, selectedComponent, h]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const loadScenarios = useCallback(() => {
    const params = new URLSearchParams();
    if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
    if (selectedCohort && selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
    authedFetch(`${API_URL}/api/people-engine/pe-scenario?${params}`, h)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setScenarios(d?.scenarios || [])).catch(() => setScenarios([]));
  }, [organizationId, selectedCohort, h]);
  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  const applyScenario = useCallback((sc) => {
    const l = sc?.lift || {};
    setLift({ capability: l.capability || 0, motivation: l.motivation || 0, retention: l.retention || 0 });
    toast.success(`Loaded "${sc.name}"`);
  }, []);

  const deleteScenario = useCallback(async (id) => {
    const params = new URLSearchParams();
    if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/pe-scenario/${id}?${params}`, { method: 'DELETE', headers: h.headers });
      if (res.ok) { setScenarios((prev) => prev.filter((s) => s.id !== id)); toast.success('Scenario deleted.'); }
      else toast.error('Could not delete the scenario.');
    } catch { toast.error('Could not delete the scenario.'); }
  }, [organizationId, h]);

  const rebaseScenario = useCallback(async (sc) => {
    const params = new URLSearchParams();
    if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/pe-scenario/${sc.id}/rebase?${params}`, { method: 'POST', headers: h.headers });
      if (res.ok) {
        const updated = await res.json();
        setScenarios((prev) => prev.map((s) => (s.id === sc.id ? updated : s)));
        toast.success(`"${sc.name}" rebased to today's PE (${updated.base_pe}% → ${updated.scenario_pe}%)`);
      } else toast.error('Could not rebase the scenario.');
    } catch { toast.error('Could not rebase the scenario.'); }
  }, [organizationId, h]);

  const rebaseAllScenarios = useCallback(async () => {
    const params = new URLSearchParams();
    if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/pe-scenario/rebase-all?${params}`, { method: 'POST', headers: h.headers });
      if (res.ok) {
        const d = await res.json();
        setScenarios(d.scenarios || []);
        toast.success(`Rebased ${d.rebased} scenario${d.rebased !== 1 ? 's' : ''} to today's PE.`);
      } else toast.error('Could not rebase the scenarios.');
    } catch { toast.error('Could not rebase the scenarios.'); }
  }, [organizationId, h]);

  const createGoalFromScenario = useCallback(async (sc) => {
    try {
      const res = await authedFetch(`${API_URL}/api/goals`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          goal_type: 'scenario_planning',
          metric: 'people_engine_pe',
          title: `PE lift: ${sc.name}`,
          description: `Lift PE from ${sc.base_pe}% to ${sc.scenario_pe}% (+${sc.lift_pts} pts) over ${sc.horizon} months via ${Object.entries(sc.lift || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k} +${v}`).join(', ') || 'targeted drivers'}.`,
          target_value: sc.scenario_pe,
          baseline_value: sc.base_pe,
          timeframe: 'quarterly',
        }),
      });
      if (res.ok) toast.success(`Goal created from "${sc.name}"`, { action: { label: 'View goals', onClick: () => navigate('/goals') } });
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Could not create the goal.'); }
    } catch { toast.error('Could not create the goal.'); }
  }, [h, navigate]);

  const generateInsight = useCallback(async (force) => {
    setInsightLoading(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/pe-trend-insight`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: organizationId, cohort_id: selectedCohort, force: !!force }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d?.available) { setInsight(d.insight); setInsightCached(!!d.cached); if (showHistory) loadHistory(); }
        else toast.error('Not enough trend history to summarise yet.');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not generate the summary.');
      }
    } catch { toast.error('Could not generate the summary.'); }
    finally { setInsightLoading(false); }
  }, [organizationId, selectedCohort, h]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = useCallback(() => {
    const params = new URLSearchParams({ cohort_id: selectedCohort });
    if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
    authedFetch(`${API_URL}/api/people-engine/pe-trend-insight/history?${params}`, h)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setHistory(d?.history || [])).catch(() => setHistory([]));
  }, [organizationId, selectedCohort, h]);

  const toggleHistory = useCallback(() => {
    setShowHistory((prev) => {
      const next = !prev;
      if (next) loadHistory();
      return next;
    });
  }, [loadHistory]);

  // Unified compare fetch: cohort/program → single overlay; period → A & B.
  useEffect(() => {
    const q = orgParam();
    if (compareDim === 'cohort') {
      setPeriodSeries(null);
      if (!compareCohort || compareCohort === 'none') { setCompareSeries(null); return; }
      authedFetch(`${API_URL}/api/people-engine/compare-series?kind=cohort&id=${encodeURIComponent(compareCohort)}&${q}`, h)
        .then((r) => (r.ok ? r.json() : null)).then(setCompareSeries).catch(() => setCompareSeries(null));
    } else if (compareDim === 'program') {
      setPeriodSeries(null);
      if (!comparePid || comparePid === 'none') { setCompareSeries(null); return; }
      authedFetch(`${API_URL}/api/people-engine/compare-series?kind=program&id=${encodeURIComponent(comparePid)}&${q}`, h)
        .then((r) => (r.ok ? r.json() : null)).then(setCompareSeries).catch(() => setCompareSeries(null));
    } else if (compareDim === 'period') {
      setCompareSeries(null);
      const cohq = selectedCohort && selectedCohort !== 'all' ? `&cohort_id=${encodeURIComponent(selectedCohort)}` : '';
      const ready = periodA.start && periodA.end && periodB.start && periodB.end;
      if (!ready) { setPeriodSeries(null); return; }
      Promise.all([
        authedFetch(`${API_URL}/api/people-engine/compare-series?kind=period&start=${periodA.start}&end=${periodA.end}&${q}${cohq}`, h).then((r) => (r.ok ? r.json() : null)),
        authedFetch(`${API_URL}/api/people-engine/compare-series?kind=period&start=${periodB.start}&end=${periodB.end}&${q}${cohq}`, h).then((r) => (r.ok ? r.json() : null)),
      ]).then(([a, b]) => setPeriodSeries({ a, b })).catch(() => setPeriodSeries(null));
    }
  }, [compareDim, compareCohort, comparePid, periodA, periodB, selectedCohort, orgParam, h]);

  const cohortLabel = useCallback((id) => (id === 'all' ? 'All Cohorts' : (cohorts.find((c) => c.id === id)?.name || id)), [cohorts]);
  const periodMode = compareDim === 'period';

  // Latest / earliest month present in the trend — anchors the period presets.
  const [latestDataMonth, earliestDataMonth] = useMemo(() => {
    let latest = null; let earliest = null;
    (trend?.trend_data || []).forEach((r) => {
      const per = r.period;
      if (per) {
        if (!latest || per > latest) latest = per;
        if (!earliest || per < earliest) earliest = per;
      }
    });
    return [latest, earliest];
  }, [trend]);

  const yearPresetOk = !!(latestDataMonth && earliestDataMonth && Number(earliestDataMonth.slice(0, 4)) < Number(latestDataMonth.slice(0, 4)));

  const shiftMonth = (ym, delta) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const monthsBetween = (a, b) => {
    const out = []; let cur = a;
    for (let i = 0; i < 60 && cur <= b; i++) { out.push(cur); cur = shiftMonth(cur, 1); }
    return out;
  };
  const applyPreset = useCallback((preset) => {
    const anchor = latestDataMonth || new Date().toISOString().slice(0, 7);
    const earliest = earliestDataMonth || shiftMonth(anchor, -11);
    setCompareDim('period');
    if (preset === 'quarter') {
      setPeriodB({ start: shiftMonth(anchor, -2), end: anchor });
      setPeriodA({ start: shiftMonth(anchor, -5), end: shiftMonth(anchor, -3) });
    } else if (preset === 'year') {
      const anchorYear = Number(anchor.slice(0, 4));
      const anchorMo = anchor.slice(5, 7);
      // True year-over-year: the SAME calendar months of the prior year (YTD) vs
      // this year, clamped to the earliest data and trimmed to equal length.
      let aStart = `${anchorYear - 1}-01`;
      const aEnd = `${anchorYear - 1}-${anchorMo}`;
      if (aStart < earliest) aStart = earliest;
      let aMonths = aStart <= aEnd ? monthsBetween(aStart, aEnd) : [];
      let bMonths = monthsBetween(`${anchorYear}-01`, anchor);
      if (aMonths.length >= 2) {
        const n = Math.max(1, Math.min(aMonths.length, bMonths.length));
        aMonths = aMonths.slice(aMonths.length - n);
        bMonths = bMonths.slice(bMonths.length - n);
        setPeriodA({ start: aMonths[0], end: aMonths[n - 1] });
        setPeriodB({ start: bMonths[0], end: bMonths[n - 1] });
      } else {
        // Not enough same-month prior-year data → equal earlier vs later halves.
        const months = monthsBetween(earliest, anchor);
        const half = Math.max(1, Math.floor(months.length / 2));
        setPeriodA({ start: months[0], end: months[half - 1] });
        setPeriodB({ start: months[months.length - half], end: months[months.length - 1] });
      }
    }
  }, [latestDataMonth, earliestDataMonth]);

  const onPrimaryChange = useCallback((v) => {
    setSelectedCohort(v);
    if (v === compareCohort) setCompareCohort('none');
  }, [compareCohort]);

  const buildPdf = useCallback(async () => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    const pageW = pdf.internal.pageSize.getWidth();
    const margin = 28;
    let y = margin;
    pdf.setFontSize(16); pdf.setTextColor(15, 23, 42);
    pdf.text('Capability Health — PE Trends', margin, y); y += 18;
    pdf.setFontSize(10); pdf.setTextColor(100, 116, 139);
    const cmpTxt = periodMode
      ? (periodSeries ? `  ·  A ${periodA.start}–${periodA.end} vs B ${periodB.start}–${periodB.end}` : '')
      : (compareSeries ? `  vs  ${compareSeries.label}` : '');
    pdf.text(`Cohort: ${cohortLabel(selectedCohort)}${cmpTxt}  ·  ${new Date().toLocaleDateString()}`, margin, y); y += 12;
    const refs = [compositeRef.current, byComponentRef.current].filter(Boolean);
    for (let i = 0; i < refs.length; i++) {
      const canvas = await html2canvas(refs[i], { scale: 1.5, backgroundColor: '#ffffff', logging: false });
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height / canvas.width) * imgW;
      if (i > 0) { pdf.addPage(); y = margin; }
      pdf.addImage(canvas.toDataURL('image/jpeg', 0.82), 'JPEG', margin, y, imgW, imgH);
    }
    return pdf;
  }, [selectedCohort, periodMode, periodSeries, periodA, periodB, compareSeries, cohortLabel]);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    try { (await buildPdf()).save(`pe-capability-trends-${selectedCohort}.pdf`); }
    catch (e) { toast.error('Could not export the PDF. Please try again.'); }
    finally { setExporting(false); }
  }, [buildPdf, selectedCohort]);

  const emailReportNow = useCallback(async (recipients) => {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient email.'); return false; }
    setEmailSending(true);
    try {
      const pdf = await buildPdf();
      const b64 = pdf.output('datauristring');
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/people-engine/email-report`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: list, pdf_base64: b64, filename: `pe-capability-trends-${selectedCohort}.pdf`, scope_label: cohortLabel(selectedCohort) }),
      });
      if (res.ok) { toast.success(`Report emailed to ${list.length} recipient(s).`); return true; }
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Could not send the report.');
      return false;
    } catch (e) { toast.error('Could not send the report.'); return false; }
    finally { setEmailSending(false); }
  }, [buildPdf, selectedCohort, cohortLabel]);

  const saveSchedule = useCallback(async (enabled, recipients) => {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    const token = secureStorage.getItem('session_token');
    await authedFetch(`${API_URL}/api/people-engine/report-schedule`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId, enabled, recipients: list, cohort_id: selectedCohort }),
    });
    toast.success(enabled ? 'Monthly auto-send enabled.' : 'Monthly auto-send turned off.');
  }, [organizationId, selectedCohort]);

  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Composite graph data: month, PE + component scores, merged with compare overlay.
  const compareActive = !periodMode && !!compareSeries;
  const compareHasPillars = compareActive && !!compareSeries?.has_pillars;
  const compareLabel = compareSeries?.label || '';
  const compositeData = useMemo(() => {
    const cmpByMonth = {};
    if (compareActive) {
      (compareSeries?.trend_data || []).forEach((t) => { cmpByMonth[t.month] = t; });
    }
    return (trend?.trend_data || []).map((t) => {
      const row = { month: t.month, score: Math.round(t.score) };
      COMPONENT_LINES.forEach(({ key }) => { if (typeof t[key] === 'number') row[key] = Math.round(t[key]); });
      if (compareActive) {
        const ct = cmpByMonth[t.month];
        if (ct) {
          if (typeof ct.score === 'number') row.c_score = Math.round(ct.score);
          if (compareHasPillars) COMPONENT_LINES.forEach(({ key }) => { if (typeof ct[key] === 'number') row[`c_${key}`] = Math.round(ct[key]); });
        }
      }
      return row;
    });
  }, [trend, compareSeries, compareActive, compareHasPillars]);

  const periodData = useMemo(() => {
    if (!periodMode || !periodSeries?.a || !periodSeries?.b) return [];
    const a = periodSeries.a.trend_data || [];
    const b = periodSeries.b.trend_data || [];
    const n = Math.max(a.length, b.length);
    const out = [];
    for (let i = 0; i < n; i++) {
      out.push({
        idx: `Month ${i + 1}`,
        a: a[i] ? Math.round(a[i].score) : null,
        b: b[i] ? Math.round(b[i].score) : null,
        aMonth: a[i]?.month, bMonth: b[i]?.month,
      });
    }
    return out;
  }, [periodMode, periodSeries]);

  const activeComponents = COMPONENT_LINES.filter(({ key }) => compositeData.length && typeof compositeData[0][key] === 'number');
  const primaryLabel = cohortLabel(selectedCohort);
  const visibleKeys = ['score', 'c_score', ...activeComponents.flatMap((p) => [p.key, `c_${p.key}`])].filter((k) => !hidden.has(k));
  const compVals = compositeData.flatMap((r) => visibleKeys.map((k) => r[k]).filter((v) => typeof v === 'number'));
  const compDomain = compVals.length
    ? [Math.min(55, Math.max(0, Math.floor(Math.min(...compVals)) - 4)), Math.min(100, Math.ceil(Math.max(...compVals)) + 4)]
    : [0, 100];

  const allEvents = (trend?.trend_annotations || []).flatMap((a) => a.events || []);
  const categories = ['all',
    ...[...new Set(allEvents.filter((e) => e.type === 'program' && e.program_type).map((e) => e.program_type))],
    ...(allEvents.some((e) => e.type === 'content') ? ['content'] : []),
  ];
  const matchesFilter = (e) => annoFilter === 'all' || (annoFilter === 'content' ? e.type === 'content' : e.program_type === annoFilter);
  const annos = (trend?.trend_annotations || [])
    .filter((a) => compositeData.some((d) => d.month === a.month))
    .map((a) => {
      const evs = (a.events || []).filter(matchesFilter);
      if (!evs.length) return null;
      const firstProgram = evs.find((e) => e.program_id);
      return { month: a.month, events: evs, label: a.label, detail: a.detail, type: a.type, target: firstProgram ? firstProgram.program_id : null };
    })
    .filter(Boolean);

  const onMarkerClick = (a) => {
    if (!navigate || !a.target) return;
    navigate(`/program-analytics?program_id=${encodeURIComponent(a.target)}`);
  };

  // ── Diagnostic layer: biggest mover, per-component contribution, inflection ──
  const movers = useMemo(() => {
    if (compositeData.length < 2) return null;
    const f = compositeData[0], l = compositeData[compositeData.length - 1];
    const comps = activeComponents.map(({ key, name, color }) => ({ key, name, color, now: l[key], delta: Math.round(((l[key] || 0) - (f[key] || 0)) * 10) / 10 }));
    const top = [...comps].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))[0];
    const peDelta = Math.round(((l.score || 0) - (f.score || 0)) * 10) / 10;
    // biggest single month-over-month composite drop → the "where"
    let dip = null;
    for (let i = 1; i < compositeData.length; i++) {
      const d = (compositeData[i].score || 0) - (compositeData[i - 1].score || 0);
      if (dip === null || d < dip.delta) dip = { month: compositeData[i].month, delta: Math.round(d * 10) / 10 };
    }
    return { comps, top, peDelta, months: compositeData.length, dip: dip && dip.delta < -0.5 ? dip : null };
  }, [compositeData, activeComponents]);

  // ── Forecast: project composite PE + components 4 months ahead (linear fit +
  // a confidence band from historical volatility widening over the horizon). ──
  const forecastData = useMemo(() => {
    const rows = (trend?.trend_data || []).filter((r) => typeof r.score === 'number');
    if (rows.length < 3) return null;
    const HK = ['score', 'capability', 'motivation', 'retention'];
    const fit = {};
    HK.forEach((k) => {
      const ys = rows.map((r) => r[k]).filter((v) => typeof v === 'number');
      const n = ys.length;
      if (n < 3) return;
      const xs = ys.map((_, i) => i);
      const mx = (n - 1) / 2, my = ys.reduce((a, b) => a + b, 0) / n;
      let num = 0, den = 0;
      xs.forEach((x, i) => { num += (x - mx) * (ys[i] - my); den += (x - mx) ** 2; });
      const slope = den ? num / den : 0, intercept = my - slope * mx;
      const resid = ys.map((y, i) => y - (intercept + slope * i));
      const sigma = Math.sqrt(resid.reduce((a, b) => a + b * b, 0) / n) || 2;
      fit[k] = { slope, intercept, sigma, n };
    });
    const H = 4;
    const shiftPeriod = (ym, d) => { const [y, m] = ym.split('-').map(Number); const dt = new Date(y, m - 1 + d, 1); return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()]} ${String(dt.getFullYear()).slice(2)}`; };
    const lastPeriod = rows[rows.length - 1].period;
    const hist = rows.map((r) => ({ month: r.month, actual: Math.round(r.score) }));
    const fc = [];
    const f = fit.score;
    for (let i = 1; i <= H; i++) {
      const idx = f.n - 1 + i;
      const yhat = Math.max(0, Math.min(100, f.intercept + f.slope * idx));
      const band = Math.min(20, 1.5 * f.sigma * Math.sqrt(i));
      fc.push({
        month: lastPeriod ? shiftPeriod(lastPeriod, i) : `+${i}`,
        forecast: Math.round(yhat),
        lower: Math.round(Math.max(0, yhat - band)),
        upper: Math.round(Math.min(100, yhat + band)),
        band: [Math.round(Math.max(0, yhat - band)), Math.round(Math.min(100, yhat + band))],
        cap_f: fit.capability ? Math.round(fit.capability.intercept + fit.capability.slope * idx) : null,
        mot_f: fit.motivation ? Math.round(fit.motivation.intercept + fit.motivation.slope * idx) : null,
        ret_f: fit.retention ? Math.round(fit.retention.intercept + fit.retention.slope * idx) : null,
      });
    }
    // connect the actual line into the forecast by seeding the first forecast row with last actual
    const bridged = [...hist.map((h) => ({ ...h })), ...fc];
    if (bridged.length > hist.length) bridged[hist.length].actual = hist[hist.length - 1].actual;
    const endScore = fc[H - 1].forecast;
    const nowScore = hist[hist.length - 1].actual;
    return { data: bridged, endScore, nowScore, delta: endScore - nowScore, horizon: H, histLen: hist.length };
  }, [trend]);

  // ── What-if scenario: apply an intervention lift (pts) to the forecast
  // component trajectories (ramped in over the horizon) and re-derive the
  // composite PE with the model PE = (((C·M·R) + LEI) / 2), holding the
  // implied Learning Effectiveness fixed. Lets leaders model "+X pts on
  // Motivation/Capability" and see the projected PE line shift live. ──
  const scenarioForecast = useMemo(() => {
    if (!forecastData || !scenarioActive) return null;
    const { horizon: H, histLen } = forecastData;
    let step = 0;
    const data = forecastData.data.map((row, idx) => {
      if (row.forecast == null) return { ...row };
      step += 1;
      const ramp = step / H;
      const capL = Math.min(100, (row.cap_f ?? 0) + lift.capability * ramp);
      const motL = Math.min(100, (row.mot_f ?? 0) + lift.motivation * ramp);
      const retL = Math.min(100, (row.ret_f ?? 0) + lift.retention * ramp);
      const c = (row.cap_f ?? 0) / 100, m = (row.mot_f ?? 0) / 100, r = (row.ret_f ?? 0) / 100, pe = (row.forecast ?? 0) / 100;
      const impliedLei = Math.max(0, Math.min(1, 2 * pe - c * m * r));
      const scenPe = (((capL / 100) * (motL / 100) * (retL / 100)) + impliedLei) / 2 * 100;
      return { ...row, scenario: Math.round(Math.max(0, Math.min(100, scenPe))) };
    });
    // Anchor the scenario line to the last actual so it visually branches off.
    if (data[histLen]) data[histLen].scenario = data[histLen].actual;
    const endScenario = data[data.length - 1].scenario;
    return { data, endScenario, lift: endScenario - forecastData.endScore };
  }, [forecastData, lift, scenarioActive]);

  const forecastChartData = scenarioActive && scenarioForecast ? scenarioForecast.data : forecastData?.data;

  // Overlay data: base forecast rows + live scenario + up to 2 saved-scenario
  // comparison lines (merged by month) for the Scenario Compare view.
  const compareScenarios = useMemo(
    () => compareIds.map((id) => scenarios.find((s) => s.id === id)).filter(Boolean),
    [compareIds, scenarios]);
  const forecastMerged = useMemo(() => {
    if (!forecastData) return [];
    const liveMap = scenarioActive ? computeScenarioByMonth(forecastData, lift) : {};
    const cmpMaps = compareScenarios.map((sc) => computeScenarioByMonth(forecastData, sc.lift || {}));
    return forecastData.data.map((row) => {
      const r = { ...row };
      if (scenarioActive && liveMap[row.month] != null) r.scenario = liveMap[row.month];
      cmpMaps.forEach((mp, i) => { if (mp[row.month] != null) r[`cmp_${i}`] = mp[row.month]; });
      return r;
    });
  }, [forecastData, scenarioActive, lift, compareScenarios]);

  const saveScenario = useCallback(async () => {
    const name = scenarioName.trim();
    if (!name) { toast.error('Give the scenario a name.'); return; }
    if (!forecastData || !scenarioForecast) return;
    setSavingScenario(true);
    try {
      const res = await authedFetch(`${API_URL}/api/people-engine/pe-scenario`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: organizationId, cohort_id: selectedCohort, name, lift,
          base_pe: forecastData.endScore, scenario_pe: scenarioForecast.endScenario, horizon: forecastData.horizon,
          cost: scenarioCost === '' ? undefined : Number(scenarioCost),
        }),
      });
      if (res.ok) {
        toast.success(`Saved "${name}"`);
        setShowSaveScenario(false); setScenarioName(''); setScenarioCost('');
        loadScenarios();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Could not save the scenario.');
      }
    } catch { toast.error('Could not save the scenario.'); }
    finally { setSavingScenario(false); }
  }, [scenarioName, forecastData, scenarioForecast, organizationId, selectedCohort, lift, h, loadScenarios, scenarioCost]);

  // ── By-component drill-in graph ──────────────────────────────
  const detailSeries = COMPONENT_DETAIL[selectedComponent]?.series || [];
  const detailChartData = useMemo(() => {
    const rows = detail?.trend || [];
    // Determine auto-scale (×100) when all sampled values are ≤ 1.
    const autoScale = {};
    detailSeries.forEach((s) => {
      if (s.scale === 'auto') {
        const vals = rows.map((r) => r[s.key]).filter((v) => typeof v === 'number');
        autoScale[s.key] = vals.length && Math.max(...vals) <= 1 ? 100 : 1;
      }
    });
    return rows.map((r) => {
      const out = { month: r.period };
      detailSeries.forEach((s) => {
        const raw = r[s.key];
        if (typeof raw === 'number') {
          const factor = s.scale === 'auto' ? autoScale[s.key] : s.scale;
          out[s.key] = Math.round(raw * factor * 10) / 10;
        }
      });
      return out;
    });
  }, [detail, detailSeries]);

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="pe-trends-full-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const currentPe = trend?.overall_score;

  return (
    <div className="space-y-6" data-testid="pe-trends-full">
      {!onlyByComponent && (<>
      {/* Cohort filter + compare + export + current PE */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <Users className="h-4 w-4 text-indigo-500" /> Filter by Cohort
            </label>
            <Select value={selectedCohort} onValueChange={onPrimaryChange}>
              <SelectTrigger className="w-56" data-testid="pe-trends-full-cohort-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cohorts (portfolio)</SelectItem>
                {cohorts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <GitCompare className="h-4 w-4 text-fuchsia-500" /> Compare by
            </label>
            <div className="flex gap-2">
              <Select value={compareDim} onValueChange={setCompareDim}>
                <SelectTrigger className="w-32" data-testid="pe-trends-full-compare-dim"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cohort">Cohort</SelectItem>
                  <SelectItem value="program">Program</SelectItem>
                  <SelectItem value="period">Time period</SelectItem>
                </SelectContent>
              </Select>
              {compareDim === 'cohort' && (
                <Select value={compareCohort} onValueChange={setCompareCohort}>
                  <SelectTrigger className="w-52" data-testid="pe-trends-full-compare-filter"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="all">All Cohorts (portfolio)</SelectItem>
                    {cohorts.filter((c) => c.id !== selectedCohort).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {compareDim === 'program' && (
                <Select value={comparePid} onValueChange={setComparePid}>
                  <SelectTrigger className="w-52" data-testid="pe-trends-full-compare-program"><SelectValue placeholder="Pick a program" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
        {compareDim === 'period' && (
          <div className="flex flex-wrap items-end gap-3" data-testid="pe-trends-full-period-inputs">
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold text-slate-500">Quick presets</span>
              <div className="flex gap-1.5">
                <button onClick={() => applyPreset('quarter')} data-testid="pe-period-preset-quarter"
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:border-fuchsia-400 hover:text-fuchsia-700 transition-colors">Last qtr vs prior qtr</button>
                <button onClick={() => applyPreset('year')} data-testid="pe-period-preset-year"
                  title={yearPresetOk ? 'Compare this year to last year' : 'Compares the earlier vs later half of your available history'}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:border-fuchsia-400 hover:text-fuchsia-700 transition-colors">This year vs last year</button>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-fuchsia-700 mb-1 block">Range A</label>
              <div className="flex items-center gap-1">
                <input type="month" value={periodA.start} onChange={(e) => setPeriodA((s) => ({ ...s, start: e.target.value }))} data-testid="pe-period-a-start" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                <span className="text-slate-400 text-xs">→</span>
                <input type="month" value={periodA.end} onChange={(e) => setPeriodA((s) => ({ ...s, end: e.target.value }))} data-testid="pe-period-a-end" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-indigo-700 mb-1 block">Range B</label>
              <div className="flex items-center gap-1">
                <input type="month" value={periodB.start} onChange={(e) => setPeriodB((s) => ({ ...s, start: e.target.value }))} data-testid="pe-period-b-start" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                <span className="text-slate-400 text-xs">→</span>
                <input type="month" value={periodB.end} onChange={(e) => setPeriodB((s) => ({ ...s, end: e.target.value }))} data-testid="pe-period-b-end" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
              </div>
            </div>
          </div>
        )}
        <div className="flex items-end gap-3">
          <button onClick={() => setShowEmailDialog(true)} data-testid="pe-trends-full-email-report"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-colors">
            <Mail className="h-4 w-4" /> Email report
          </button>
          <button onClick={exportPdf} disabled={exporting} data-testid="pe-trends-full-export-pdf"
            className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors',
              exporting ? 'opacity-60 border-slate-200 text-slate-400' : 'border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700')}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
          {currentPe != null && (
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-right" data-testid="pe-trends-full-current">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Current PE{compareActive ? ` · ${primaryLabel}` : ''}</p>
              <p className="text-3xl font-extrabold tabular-nums text-slate-800">{Math.round(currentPe * 10) / 10}%</p>
              {compareActive && compareSeries?.overall_score != null && (
                <p className="text-xs font-semibold text-fuchsia-600 mt-0.5" data-testid="pe-trends-full-compare-current">
                  {compareLabel}: {Math.round(compareSeries.overall_score * 10) / 10}%
                  {compareSeries?.modeled && (
                    <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide bg-slate-100 text-slate-500 border border-slate-200" data-testid="pe-trends-full-compare-modeled" title="This overlay uses a modeled (seeded) trend — the entity has under two months of real history.">Modeled</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Composite graph — PE + component lines + program impact markers */}
      <div ref={compositeRef}>
      <Card data-testid="pe-trends-full-composite-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5 text-indigo-600" /> Composite PE &amp; Component Trends
          </CardTitle>
          <p className="text-xs text-slate-500">People Engine capacity with Capability / Motivation / Retention lines and program-impact markers. Click the legend to toggle a line.{trend?.modeled ? ' Trend is modeled from a seeded baseline (import ≥2 months of data for live values).' : ''}</p>
        </CardHeader>
        <CardContent>
          {periodMode ? (
            (periodSeries?.a?.trend_data?.length || 0) >= 2 && (periodSeries?.b?.trend_data?.length || 0) >= 2 ? (
              <>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={periodData} margin={{ top: 20, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="idx" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                  <YAxis allowDecimals={false} tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" domain={['dataMin - 4', 'dataMax + 4']} />
                  <RTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
                    formatter={(v, n, p) => [`${v}%`, n === 'a' ? `A · ${p?.payload?.aMonth || ''}` : `B · ${p?.payload?.bMonth || ''}`]} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="plainline" />
                  <Line type="monotone" dataKey="a" name={`Range A (${periodSeries?.a?.label || ''})`} stroke="#c026d3" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                  <Line type="monotone" dataKey="b" name={`Range B (${periodSeries?.b?.label || ''})`} stroke="#4f46e5" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-[10px] text-slate-400 mt-2">Overall PE per month computed within each window — figures may differ slightly from the anchored "Current PE" shown elsewhere.</p>
              </>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-500 text-sm text-center px-6" data-testid="pe-trends-full-period-empty">
                {!periodSeries ? 'Pick two date ranges (Range A and Range B) above to compare periods.'
                  : 'Not enough monthly history to compare — pick wider windows.'}
              </div>
            )
          ) : (<>
          {categories.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="pe-trends-full-anno-filter">
              <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Program events</span>
              {categories.map((cat) => (
                <button key={cat} onClick={() => setAnnoFilter(cat)} data-testid={`pe-trends-full-anno-filter-${cat}`}
                  className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors',
                    annoFilter === cat ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-transparent text-slate-500 border-slate-200 hover:border-indigo-400 hover:text-indigo-600')}>
                  {ANNO_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          )}
          {movers && movers.top && (
            <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5" data-testid="pe-trends-biggest-mover">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold" style={{ color: movers.top.color }}>{movers.top.name} {movers.top.delta >= 0 ? '+' : ''}{movers.top.delta} pts</span>
                  <span className="text-slate-500"> over {movers.months} months — the biggest {movers.peDelta >= 0 ? 'driver of' : 'drag on'} PE (</span>
                  <span className={cn('font-semibold', movers.peDelta >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{movers.peDelta >= 0 ? '+' : ''}{movers.peDelta} pts</span>
                  <span className="text-slate-500">)</span>
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowContrib((s) => !s)} data-testid="pe-trends-contrib-toggle"
                    className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-slate-300 text-slate-600 hover:border-indigo-400 hover:text-indigo-700 transition-colors">
                    {showContrib ? 'Hide contribution' : 'Show contribution'}
                  </button>
                  {navigate && (
                    <button onClick={() => navigate('/people-engine/deep-dives', { state: { focusComponent: movers.top.key } })} data-testid="pe-trends-mover-action"
                      className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors">Open action plan</button>
                  )}
                </div>
              </div>
              {showContrib && (
                <div className="mt-2.5 flex flex-wrap gap-2" data-testid="pe-trends-contrib">
                  {[...movers.comps].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).map((c) => (
                    <span key={c.key} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full bg-white border border-slate-200">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />{c.name}
                      <span className={c.delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}>{c.delta >= 0 ? '+' : ''}{c.delta}</span>
                    </span>
                  ))}
                  <span className="text-[10px] text-slate-400 self-center">how each component moved over the window</span>
                </div>
              )}
            </div>
          )}
          {compositeData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={compositeData} margin={{ top: 34, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <ReferenceArea y1={60} y2={100} fill="#10b981" fillOpacity={0.06} strokeOpacity={0}
                  label={{ value: 'Healthy band', position: 'insideTopLeft', fill: '#10b981', fontSize: 9, fontWeight: 700 }} />
                {movers?.dip && (
                  <ReferenceLine x={movers.dip.month} stroke="#ef4444" strokeDasharray="3 3"
                    label={{ value: `Biggest dip ${movers.dip.delta}`, position: 'insideBottomRight', fill: '#ef4444', fontSize: 9, fontWeight: 700 }} />
                )}
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <YAxis domain={compDomain} allowDecimals={false} tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <RTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
                  formatter={(v, n) => [`${v}%`, n]}
                  itemSorter={(item) => (item.dataKey === 'score' ? -1 : 1)} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: 'pointer' }} iconType="plainline"
                  itemSorter={(item) => (LEGEND_RANK[item?.dataKey ?? item?.payload?.dataKey] ?? 99)}
                  onClick={(o) => toggle(o?.dataKey || o?.payload?.dataKey)}
                  formatter={(value, entry) => {
                    const key = entry?.dataKey || entry?.payload?.dataKey;
                    return <span style={{ opacity: hidden.has(key) ? 0.4 : 1 }} data-testid={`pe-trends-full-legend-${key}`}>{value}</span>;
                  }} />
                {annos.map((a, i) => {
                  const color = ANNO_COLOR[a.type] || ANNO_COLOR.mixed;
                  return (
                    <ReferenceLine key={`${a.month}-${i}`} x={a.month} stroke={color} strokeDasharray="4 3"
                      label={<AnnotationMarker anno={a} color={color} index={i} last={i === annos.length - 1} onClick={a.target ? () => onMarkerClick(a) : undefined} />} />
                  );
                })}
                <Line type="monotone" dataKey="score" name={compareActive ? `PE Score — ${primaryLabel}` : 'PE Score'} stroke="#0f172a" strokeWidth={3} dot={{ r: 3, fill: '#0f172a' }} activeDot={{ r: 5 }} hide={hidden.has('score')} />
                {activeComponents.map(({ key, color, name }) => (
                  <Line key={key} type="monotone" dataKey={key} name={name}
                    stroke={color} strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 4 }} hide={hidden.has(key)} />
                ))}
                {compareActive && (
                  <Line type="monotone" dataKey="c_score" name={`PE Score — ${compareLabel}`} stroke="#c026d3" strokeWidth={3} strokeDasharray="2 3" dot={{ r: 3, fill: '#c026d3' }} activeDot={{ r: 5 }} hide={hidden.has('c_score')} connectNulls />
                )}
                {compareActive && compareHasPillars && activeComponents.map(({ key, color, name }) => (
                  <Line key={`c_${key}`} type="monotone" dataKey={`c_${key}`} name={`${name} — ${compareLabel}`}
                    stroke={color} strokeWidth={1.5} strokeDasharray="1 3" strokeOpacity={0.6} dot={false} activeDot={{ r: 3 }} hide={hidden.has(`c_${key}`)} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-500 text-sm">Not enough history to plot a trend for this selection.</div>
          )}
          {annos.length > 0 && (
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500" data-testid="pe-trends-full-marker-key">
              <span className="uppercase tracking-wide">Markers</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.program }} />Launch</span>
            </div>
          )}
          </>)}
        </CardContent>
      </Card>
      </div>

      {/* AI Trend Narrator — cached GPT summary of what's driving C/M/R movement */}
      <Card data-testid="pe-trends-full-insight-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-5 w-5 text-fuchsia-600" /> What's Driving This Trend
              </CardTitle>
              <p className="text-xs text-slate-500">A plain-English read on which component is moving PE and the one move to lift the weakest.</p>
            </div>
            <button onClick={() => generateInsight(!!insight)} disabled={insightLoading} data-testid="pe-trends-full-insight-generate"
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors shrink-0',
                insightLoading ? 'opacity-60 border-slate-200 text-slate-400' : 'border-fuchsia-300 text-fuchsia-700 hover:border-fuchsia-500 hover:bg-fuchsia-50')}>
              {insightLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : (insight ? <RefreshCw className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />)}
              {insightLoading ? 'Generating…' : (insight ? 'Refresh' : 'Generate summary')}
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {insight ? (
            <div data-testid="pe-trends-full-insight-text">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{insight}</p>
              <div className="flex items-center gap-3 mt-2">
                {insightCached && <p className="text-[10px] text-slate-400">Cached — click Refresh to regenerate from the latest numbers.</p>}
                <button onClick={toggleHistory} data-testid="pe-trends-full-insight-history-toggle"
                  className="text-[11px] font-semibold text-fuchsia-600 hover:text-fuchsia-700 inline-flex items-center gap-1 transition-colors">
                  <History className="h-3.5 w-3.5" /> {showHistory ? 'Hide previous reads' : 'Previous reads'}
                </button>
              </div>
              {showHistory && (
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid="pe-trends-full-insight-history">
                  {history.length ? (
                    <>
                      <p className="text-[10px] text-slate-400 mb-2">Tick two reads to compare them side by side.</p>
                      {compareSel.length === 2 && (
                        <div className="grid grid-cols-2 gap-3 mb-3" data-testid="pe-trends-full-insight-compare">
                          {compareSel.map((idx) => {
                            const hh = history[idx];
                            return (
                              <div key={idx} className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/10 p-2.5">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-500 mb-1">{hh?.generated_at ? new Date(hh.generated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Earlier'}</p>
                                <p className="text-[11px] text-slate-600 leading-relaxed whitespace-pre-line">{hh?.insight}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="space-y-3">
                        {history.map((hh, i) => (
                          <div key={i} className="text-xs flex gap-2" data-testid={`pe-insight-history-item-${i}`}>
                            <input type="checkbox" checked={compareSel.includes(i)} onChange={() => toggleCompare(i)} className="mt-0.5 accent-fuchsia-600" data-testid={`pe-insight-history-check-${i}`} />
                            <div className="min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">{hh.generated_at ? new Date(hh.generated_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Earlier'}</p>
                              <p className="text-slate-600 leading-relaxed whitespace-pre-line">{hh.insight}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : <p className="text-xs text-slate-400">No earlier summaries yet — each time you Refresh, the previous read is kept here.</p>}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400" data-testid="pe-trends-full-insight-empty">No summary yet. Click "Generate summary" for an AI read of the trend.</p>
          )}
        </CardContent>
      </Card>

      {/* Separate graph — PE Forecast (project the next months + confidence band) */}
      </>)}
      <div ref={byComponentRef}>
      <Card data-testid="pe-trends-full-forecast-card">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-5 w-5 text-violet-600" /> PE Forecast
              </CardTitle>
              <p className="text-xs text-slate-500">Where PE and its components are heading over the next {forecastData?.horizon || 4} months, projected from the trend with a confidence band.</p>
            </div>
            {forecastData && (
              <div className="flex items-center gap-4">
                <div className="text-right" data-testid="pe-forecast-projection">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Projected PE ({forecastData.horizon}mo)</p>
                  <p className="text-2xl font-extrabold tabular-nums text-slate-800">{forecastData.endScore}%
                    <span className={cn('ml-2 text-sm font-semibold', forecastData.delta >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{forecastData.delta >= 0 ? '+' : ''}{forecastData.delta}</span>
                  </p>
                </div>
                {scenarioActive && scenarioForecast && (
                  <div className="text-right pl-4 border-l border-slate-200" data-testid="pe-forecast-scenario-projection">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-600">With intervention</p>
                    <p className="text-2xl font-extrabold tabular-nums text-emerald-600">{scenarioForecast.endScenario}%
                      <span className="ml-2 text-sm font-semibold text-emerald-600">{scenarioForecast.lift >= 0 ? '+' : ''}{scenarioForecast.lift} vs base</span>
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {forecastData && (
            <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50/50 px-4 py-3" data-testid="pe-forecast-scenario-controls">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs font-bold uppercase tracking-wide text-violet-700 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> What-if intervention
                </p>
                <div className="flex items-center gap-3">
                  {scenarioActive && (
                    <button onClick={() => setShowSaveScenario(true)} data-testid="pe-forecast-scenario-save"
                      className="text-[11px] font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1 transition-colors">
                      <Save className="h-3.5 w-3.5" /> Save scenario
                    </button>
                  )}
                  {scenarioActive && (
                    <button onClick={resetLift} data-testid="pe-forecast-scenario-reset"
                      className="text-[11px] font-semibold text-slate-500 hover:text-violet-700 transition-colors">Reset</button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mb-3">Model a program's projected lift on each driver and watch the PE forecast branch live.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[['capability', 'Capability', '#6366f1'], ['motivation', 'Motivation', '#f59e0b'], ['retention', 'Retention', '#10b981']].map(([key, label, color]) => (
                  <div key={key} data-testid={`pe-forecast-lift-${key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-semibold text-slate-600 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />{label}
                      </span>
                      <span className="text-[11px] font-bold tabular-nums" style={{ color }}>+{lift[key]} pts</span>
                    </div>
                    <input type="range" min={0} max={20} step={1} value={lift[key]}
                      onChange={(e) => setLift((s) => ({ ...s, [key]: Number(e.target.value) }))}
                      data-testid={`pe-forecast-lift-slider-${key}`}
                      className="w-full h-1.5 rounded-full appearance-none cursor-pointer bg-slate-200 accent-violet-600" />
                  </div>
                ))}
              </div>
              {scenarios.length > 0 && (
                <div className="mt-3 pt-3 border-t border-violet-200/70" data-testid="pe-forecast-saved-scenarios">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-violet-700">Saved scenarios</p>
                    <div className="flex items-center gap-2">
                      <button onClick={rebaseAllScenarios} data-testid="pe-scenario-rebase-all"
                        className="text-[10px] font-semibold text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1 transition-colors" title="Rebase every scenario's baseline to today's PE">
                        <RefreshCw className="h-3 w-3" /> Rebase all
                      </button>
                      {compareIds.length > 0 && (
                        <span className="text-[10px] text-slate-500">Comparing {compareIds.length}/2 · <button onClick={() => setCompareIds([])} className="font-semibold text-violet-700 hover:underline" data-testid="pe-scenario-compare-clear">clear</button></span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {scenarios.map((sc) => {
                      const cmpIdx = compareIds.indexOf(sc.id);
                      const active = cmpIdx !== -1;
                      const nowPE = forecastData?.nowScore;
                      const denom = (sc.scenario_pe || 0) - (sc.base_pe || 0);
                      const gap = (denom > 0 && nowPE != null)
                        ? Math.max(0, Math.min(100, ((nowPE - (sc.base_pe || 0)) / denom) * 100))
                        : null;
                      return (
                      <div key={sc.id} className="flex flex-col gap-1 text-[11px] rounded-lg bg-white border border-violet-200 px-2 py-1.5"
                        data-testid={`pe-scenario-chip-${sc.id}`}>
                        <div className="flex items-center gap-2 w-full">
                        {active && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: CMP_COLORS[cmpIdx] }} />}
                        <button onClick={() => applyScenario(sc)} className="font-semibold text-slate-700 hover:text-violet-700 transition-colors truncate" data-testid={`pe-scenario-load-${sc.id}`} title="Load this scenario onto the sliders">
                          {sc.name} <span className="text-emerald-600">+{sc.lift_pts}</span>
                        </button>
                        {sc.cost_per_point != null && (
                          <span className="text-[10px] text-slate-500 tabular-nums shrink-0" title={`${sc.cost != null ? `$${Number(sc.cost).toLocaleString()} program cost` : ''}`} data-testid={`pe-scenario-cpp-${sc.id}`}>
                            ${sc.cost_per_point >= 1000 ? `${Math.round(sc.cost_per_point / 1000)}k` : Math.round(sc.cost_per_point)}/pt
                          </span>
                        )}
                        <div className="ml-auto flex items-center gap-1.5 shrink-0">
                          <button onClick={() => toggleCompareScenario(sc.id)} data-testid={`pe-scenario-compare-${sc.id}`}
                            className={cn('inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border transition-colors',
                              active ? 'bg-violet-600 text-white border-violet-600' : 'border-slate-200 text-slate-500 hover:border-violet-400')}
                            title="Overlay on the forecast to compare (up to 2)">
                            <GitCompare className="h-3 w-3" /> {active ? 'On' : 'Compare'}
                          </button>
                          <button onClick={() => createGoalFromScenario(sc)} data-testid={`pe-scenario-goal-${sc.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
                            title="Create a tracked goal from this scenario">
                            <Target className="h-3 w-3" /> Goal
                          </button>
                          <button onClick={() => rebaseScenario(sc)} data-testid={`pe-scenario-rebase-${sc.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border border-slate-200 text-slate-500 hover:border-cyan-400 hover:text-cyan-600 transition-colors"
                            title="Rebase baseline to today's PE (keeps the modeled lift)">
                            <RefreshCw className="h-3 w-3" /> Rebase
                          </button>
                          <button onClick={() => deleteScenario(sc.id)} className="text-slate-300 hover:text-rose-500 transition-colors" data-testid={`pe-scenario-delete-${sc.id}`} title="Delete scenario">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        </div>
                        {gap != null && (
                          <div className="flex items-center gap-2" data-testid={`pe-scenario-progress-${sc.id}`} title={`Live PE ${Math.round(nowPE)} · from ${sc.base_pe} toward ${sc.scenario_pe}`}>
                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${gap}%` }} />
                            </div>
                            <span className="text-[9px] font-semibold text-emerald-600 tabular-nums shrink-0">{Math.round(gap)}% to target</span>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {forecastData ? (
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={forecastMerged} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <ReferenceArea y1={60} y2={100} fill="#10b981" fillOpacity={0.06} strokeOpacity={0} />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <YAxis domain={[Math.min(50, ...forecastData.data.map((d) => d.lower ?? d.actual ?? 100)) - 4, 100]} tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <RTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }} formatter={(v, n) => (v == null ? ['—', n] : [`${v}%`, n])} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="plainline" />
                <Area type="monotone" dataKey="band" name="Confidence band" stroke="none" fill="#8b5cf6" fillOpacity={0.15} connectNulls />
                <Line type="monotone" dataKey="actual" name="PE (actual)" stroke="#0f172a" strokeWidth={3} dot={{ r: 3 }} connectNulls />
                <Line type="monotone" dataKey="forecast" name="PE (forecast)" stroke="#8b5cf6" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 3 }} connectNulls />
                {scenarioActive && (
                  <Line type="monotone" dataKey="scenario" name="PE (with intervention)" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                )}
                {compareScenarios.map((sc, i) => (
                  <Line key={sc.id} type="monotone" dataKey={`cmp_${i}`} name={sc.name} stroke={CMP_COLORS[i]} strokeWidth={2.5} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />
                ))}
                <Line type="monotone" dataKey="cap_f" name="Capability (fc)" stroke="#6366f1" strokeWidth={1.5} strokeDasharray="3 3" dot={false} connectNulls />
                <Line type="monotone" dataKey="mot_f" name="Motivation (fc)" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="3 3" dot={false} connectNulls />
                <Line type="monotone" dataKey="ret_f" name="Retention (fc)" stroke="#10b981" strokeWidth={1.5} strokeDasharray="3 3" dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-500 text-sm" data-testid="pe-trends-full-forecast-empty">
              <div className="text-center">
                <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                Need at least 3 months of history to forecast{selectedCohort !== 'all' ? ' for this cohort' : ''}.
              </div>
            </div>
          )}
          {forecastData && <p className="text-[10px] text-slate-400 mt-2">Projection is a linear trend fit; the shaded band widens with the forecast horizon to reflect uncertainty. Not a guarantee — use it to spot direction, not exact values.</p>}
        </CardContent>
      </Card>
      </div>

      {/* Email board-pack report dialog */}
      {!onlyByComponent && (
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md" data-testid="pe-email-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-indigo-600" /> Email board-pack report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Recipients (comma-separated)</label>
              <Input value={emailRecipients} onChange={(e) => setEmailRecipients(e.target.value)} placeholder="name@company.com, other@company.com" data-testid="pe-email-report-recipients" />
              <p className="text-[10px] text-slate-400 mt-1">Pre-filled with your org admins. Sends the current chart-rich PDF (composite PE &amp; component trends).</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold text-slate-700">Monthly auto-send</p>
                <p className="text-[11px] text-slate-400">Emails a monthly PE trends summary on the 1st.</p>
              </div>
              <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="pe-email-report-schedule-toggle" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => saveSchedule(scheduleEnabled, emailRecipients)} data-testid="pe-email-report-save-schedule"
              className="text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:border-indigo-400">Save schedule</button>
            <button onClick={async () => { const ok = await emailReportNow(emailRecipients); if (ok) setShowEmailDialog(false); }} disabled={emailSending} data-testid="pe-email-report-send-now"
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white', emailSending ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700')}>
              {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}{emailSending ? 'Sending…' : 'Send now'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
      {/* Save what-if scenario dialog */}
      <Dialog open={showSaveScenario} onOpenChange={setShowSaveScenario}>
        <DialogContent className="max-w-md" data-testid="pe-save-scenario-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Save className="h-4 w-4 text-violet-600" /> Save this scenario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Scenario name</label>
              <Input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} placeholder="e.g. Q3 Motivation push" data-testid="pe-save-scenario-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Program cost (USD) <span className="font-normal text-slate-400">— optional</span></label>
              <Input type="number" min={0} value={scenarioCost} onChange={(e) => setScenarioCost(e.target.value)} placeholder="e.g. 120000" data-testid="pe-save-scenario-cost" />
              {scenarioCost !== '' && scenarioForecast && scenarioForecast.lift > 0 && (
                <p className="text-[11px] text-slate-500 mt-1" data-testid="pe-save-scenario-cpp">
                  ≈ <b className="text-slate-700">${Math.round(Number(scenarioCost) / scenarioForecast.lift).toLocaleString()}</b> per PE point ({scenarioForecast.lift} pts projected)
                </p>
              )}
            </div>
            {forecastData && scenarioForecast && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="pe-save-scenario-preview">
                Projects PE <b className="text-slate-800">{scenarioForecast.endScenario}%</b> in {forecastData.horizon} months —{' '}
                <b className="text-emerald-600">+{scenarioForecast.lift} pts</b> above the {forecastData.endScore}% base.
                <p className="text-[11px] text-slate-400 mt-1">Lift: {Object.entries(lift).filter(([, v]) => v > 0).map(([k, v]) => `${k} +${v}`).join(', ') || '—'}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={saveScenario} disabled={savingScenario} data-testid="pe-save-scenario-confirm"
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white', savingScenario ? 'bg-violet-400' : 'bg-violet-600 hover:bg-violet-700')}>
              {savingScenario ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
